<template>
  <div class="copilot-sidebar">
    <!-- Header -->
    <div class="cs-header">
      <div class="cs-header-left">
        <span class="cs-label">Mydessk IA</span>
      </div>
      <div class="cs-header-actions">
        <button class="cs-btn-new" @click="newChat" title="Nuevo chat">
          <i class="el-icon-edit-outline"></i>
        </button>
      </div>
    </div>

    <!-- Sessions list -->
    <div class="cs-sessions">
      <template v-if="grouped.today.length">
        <div class="cs-group-label">Hoy</div>
        <div
          v-for="s in grouped.today"
          :key="s.id"
          class="cs-session-item"
          :class="{ active: s.id === currentSessionId }"
          @click="loadSession(s.id)"
        >
          <div class="cs-session-info">
            <div class="cs-session-title">{{ s.title }}</div>
          </div>
          <button
            class="cs-session-delete"
            @click.stop="deleteSession(s.id)"
            title="Eliminar"
          >
            <i class="el-icon-delete"></i>
          </button>
        </div>
      </template>

      <template v-if="grouped.week.length">
        <div class="cs-group-label">Esta semana</div>
        <div
          v-for="s in grouped.week"
          :key="s.id"
          class="cs-session-item"
          :class="{ active: s.id === currentSessionId }"
          @click="loadSession(s.id)"
        >
          <div class="cs-session-info">
            <div class="cs-session-title">{{ s.title }}</div>
          </div>
          <button
            class="cs-session-delete"
            @click.stop="deleteSession(s.id)"
            title="Eliminar"
          >
            <i class="el-icon-delete"></i>
          </button>
        </div>
      </template>

      <template v-if="grouped.month.length">
        <div class="cs-group-label">Este mes</div>
        <div
          v-for="s in grouped.month"
          :key="s.id"
          class="cs-session-item"
          :class="{ active: s.id === currentSessionId }"
          @click="loadSession(s.id)"
        >
          <div class="cs-session-info">
            <div class="cs-session-title">{{ s.title }}</div>
          </div>
          <button
            class="cs-session-delete"
            @click.stop="deleteSession(s.id)"
            title="Eliminar"
          >
            <i class="el-icon-delete"></i>
          </button>
        </div>
      </template>

      <template v-if="grouped.older.length">
        <div class="cs-group-label">Anterior</div>
        <div
          v-for="s in grouped.older"
          :key="s.id"
          class="cs-session-item"
          :class="{ active: s.id === currentSessionId }"
          @click="loadSession(s.id)"
        >
          <div class="cs-session-info">
            <div class="cs-session-title">{{ s.title }}</div>
          </div>
          <button
            class="cs-session-delete"
            @click.stop="deleteSession(s.id)"
            title="Eliminar"
          >
            <i class="el-icon-delete"></i>
          </button>
        </div>
      </template>

      <div v-if="sessions.length === 0" class="cs-empty">
        Aún no tienes conversaciones.<br />Inicia una nueva.
      </div>
    </div>

    <!-- Footer -->
    <div class="cs-footer">
      <div v-if="usage && usage.max_requests > 0" class="cs-usage">
        <div class="cs-usage-label">
          <span>Uso del mes</span>
          <span class="cs-usage-count"
            >{{ usage.requests }} / {{ usage.max_requests }}</span
          >
        </div>
        <div class="cs-usage-bar">
          <div
            class="cs-usage-fill"
            :style="{ width: usagePercent + '%' }"
            :class="{ 'cs-usage-warn': usagePercent >= 80 }"
          ></div>
        </div>
      </div>
      <div class="cs-context-label">Contexto activo</div>
      <div class="cs-context-badges">
        <span class="cs-badge cs-badge-live">Datos en vivo</span>
        <span class="cs-badge">{{ currentMonth }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CopilotSidebar",
  computed: {
    sessions() {
      return this.$store.getters["copilot/sessions"];
    },
    currentSessionId() {
      return this.$store.getters["copilot/currentSessionId"];
    },
    grouped() {
      return this.groupSessionsByDate(this.sessions);
    },
    usage() {
      // Preferir copilot/credits: se actualiza en tiempo real tras cada mensaje.
      // Fallback a aiUsage del plan store (puede estar desactualizado si no se ha enviado nada).
      const c = this.$store.getters["copilot/credits"];
      if (c && !c.unlimited && c.limit > 0) {
        return { requests: c.used, max_requests: c.limit };
      }
      const ai = this.$store.getters.aiUsage;
      if (!ai || ai.limit === null) return null; // ilimitado → no mostrar barra
      return { requests: ai.used, max_requests: ai.limit };
    },
    usagePercent() {
      if (!this.usage || !this.usage.max_requests) return 0;
      return Math.min(
        100,
        Math.round((this.usage.requests / this.usage.max_requests) * 100)
      );
    },
    currentMonth() {
      const months = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre"
      ];
      return months[new Date().getMonth()] + " " + new Date().getFullYear();
    }
  },
  methods: {
    close() {
      this.$store.dispatch("copilot/close");
    },
    newChat() {
      this.$store.dispatch("copilot/newSession");
    },
    loadSession(id) {
      this.$store.dispatch("copilot/loadSession", id);
      this.$emit("close-sidebar");
    },
    deleteSession(id) {
      this.$store.dispatch("copilot/deleteSession", id);
    },
    groupSessionsByDate(sessions) {
      const today = [];
      const week = [];
      const month = [];
      const older = [];
      const now = new Date();

      sessions.forEach(s => {
        const d = new Date(s.updated_at);
        const diffDays = Math.floor((now - d) / 86400000);
        if (diffDays === 0) today.push(s);
        else if (diffDays <= 7) week.push(s);
        else if (diffDays <= 30) month.push(s);
        else older.push(s);
      });

      return { today, week, month, older };
    }
  }
};
</script>

<style scoped lang="scss">
.copilot-sidebar {
  width: 260px;
  min-width: 260px;
  background: #f7f8f9;
  border-right: 1px solid #e4eaf6;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Header */
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e4eaf6;
}
.cs-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cs-logo {
  font-size: 15px;
  font-weight: 800;
  color: #2d2b55;
  letter-spacing: -0.5px;
}
.cs-label {
  font-size: 11px;
  color: #3ecfb4;
  font-weight: 600;
  background: #e1f5ee;
  padding: 2px 8px;
  border-radius: 10px;
}
.cs-header-actions {
  display: flex;
  gap: 4px;
}
.cs-btn-new {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  font-size: 14px;
  transition: all 0.15s;
  &:hover {
    border-color: #2d2b55;
    color: #2d2b55;
  }
}

/* Sessions */
.cs-sessions {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}
.cs-group-label {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  text-transform: uppercase;
  padding: 12px 16px 4px;
  letter-spacing: 0.5px;
}
.cs-session-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover {
    background: #eeeff2;
    .cs-session-delete {
      opacity: 1;
    }
  }
  &.active {
    background: #e8e9ed;
  }
}
.cs-session-info {
  flex: 1;
  min-width: 0;
}
.cs-session-title {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cs-session-delete {
  opacity: 0;
  border: none;
  background: none;
  color: #f56c6c;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    background: #fde2e2;
  }
}

.cs-empty {
  text-align: center;
  padding: 40px 16px;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

/* Footer */
.cs-footer {
  padding: 12px 16px;
  border-top: 1px solid #e4eaf6;
}
.cs-context-label {
  font-size: 10px;
  font-weight: 600;
  color: #909399;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.cs-context-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.cs-badge {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 10px;
  background: #e4eaf6;
  color: #606266;
  font-weight: 500;
}
.cs-badge-live {
  background: #e1f5ee;
  color: #1d9e75;
}

/* Usage indicator */
.cs-usage {
  margin-bottom: 10px;
}
.cs-usage-label {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 4px;
}
.cs-usage-count {
  color: #909399;
}
.cs-usage-bar {
  height: 4px;
  background: #e4eaf6;
  border-radius: 2px;
  overflow: hidden;
}
.cs-usage-fill {
  height: 100%;
  background: #3ecfb4;
  border-radius: 2px;
  transition: width 0.3s ease;
}
.cs-usage-fill.cs-usage-warn {
  background: #e6a23c;
}

@media (max-width: 768px) {
  .copilot-sidebar {
    display: none;
  }
}
</style>
